import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { usePermissions } from "../../PermissionsProvider";
import { ConfigService } from "../../services/ConfigService";
import { ModelConfig } from "../../model_gen/config";
import { Agent } from "../../model_gen/agent_base";
import { WorkflowService, Workflow } from "../../services/WorkflowService";

type Category = "agents" | "finetunes" | "models" | "workflows";

interface AgentSettings {
  max_iterations?: number | null;
  execution_mode?: string | null;
  high_performance?: boolean | null;
  disable_chaining?: boolean | null;
  model?: string | null;
  version?: {
    agent_version_number?: string | null;
    agent_release_name?: string | null;
  } | null;
}

interface ModelInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  companyId?: number;
  showEmptyOption?: boolean;
  intentGroupId?: number;
  modelType?: "all" | "server" | "client";
  agentSettings?: AgentSettings | null;
  onAgentParamsChange?: (params: {
    agent_id: number | null;
    settings: AgentSettings | null;
  }) => void;
}

const ModelInput: React.FC<ModelInputProps> = ({
  value,
  onChange,
  placeholder = "Select a model",
  label = "Model",
  disabled = false,
  style = {},
  companyId = -1,
  showEmptyOption = false,
  intentGroupId,
  modelType = "all",
  agentSettings: externalAgentSettings,
  onAgentParamsChange,
}) => {
  const { apiKey } = usePermissions();
  const [models, setModels] = useState<string[]>([]);
  const [modelFinetuned, setModelFinetuned] = useState<Record<string, boolean>>({});
  const [agents, setAgents] = useState<Agent[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSubagents, setShowSubagents] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category>("agents");
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const categoryInitialized = useRef(false);

  const agentSettings = useMemo(() => {
    return externalAgentSettings || {};
  }, [externalAgentSettings]);

  // Auto-detect category from value
  const detectCategory = (val?: string): Category => {
    if (!val) return "agents";
    if (val.startsWith("agent:")) return "agents";
    if (val.startsWith("workflow:")) return "workflows";
    if (modelFinetuned[val]) return "finetunes";
    return "models";
  };

  // Initialize category from value once data is loaded
  useEffect(() => {
    if (categoryInitialized.current) return;
    if (!value) return;
    if (value.startsWith("agent:")) {
      setSelectedCategory("agents");
      categoryInitialized.current = true;
    } else if (value.startsWith("workflow:")) {
      setSelectedCategory("workflows");
      categoryInitialized.current = true;
    } else if (Object.keys(modelFinetuned).length > 0) {
      setSelectedCategory(detectCategory(value));
      categoryInitialized.current = true;
    }
  }, [value, modelFinetuned]);

  // Fetch models
  useEffect(() => {
    setIsLoading(true);
    const sub = ConfigService.getModels(
      intentGroupId ?? companyId,
      null,
      apiKey
    ).subscribe(
      (modelData: ModelConfig) => {
        let modelsToDisplay: string[] = [];
        if (modelType === "server" && modelData.server_models) {
          modelsToDisplay = modelData.server_models;
        } else if (modelType === "client" && modelData.client_models) {
          modelsToDisplay = modelData.client_models;
        } else {
          modelsToDisplay = modelData.all_models || [];
        }
        setModels(modelsToDisplay);
        setModelFinetuned(modelData.model_finetuned || {});
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setIsLoading(false);
      }
    );
    return () => sub.unsubscribe();
  }, [apiKey, companyId, intentGroupId, modelType]);

  // Fetch agents
  useEffect(() => {
    if (!apiKey) return;
    const sub = ConfigService.getAgents(null, apiKey, showSubagents).subscribe(
      (response: any) => {
        const fetched = Array.isArray(response) ? response : response.agents || [];
        setAgents(fetched);
      },
      (error: any) => {
        console.error("Error fetching agents:", error);
      }
    );
    return () => sub.unsubscribe();
  }, [apiKey, showSubagents]);

  useEffect(() => {
    if (!apiKey) return;
    const sub = WorkflowService.getWorkflows(() => {}, apiKey).subscribe(
      (response: unknown) => {
        const list = Array.isArray(response) ? response : [];
        setWorkflows(list as Workflow[]);
      },
      () => {}
    );
    return () => sub.unsubscribe();
  }, [apiKey]);

  // Fetch agent releases
  const [agentReleasesMap, setAgentReleasesMap] = useState<
    Map<number, Array<{ name: string; version_number: string; is_default?: boolean }>>
  >(new Map());

  useEffect(() => {
    if (!apiKey) return;
    const sub = ConfigService.getAgentsWithReleases(null, apiKey).subscribe(
      (response: any) => {
        const releases = response.agent_releases || [];
        const newMap = new Map<number, Array<{ name: string; version_number: string; is_default?: boolean }>>();
        releases.forEach((r: any) => {
          const agentId = r.agent_id;
          if (!newMap.has(agentId)) newMap.set(agentId, []);
          newMap.get(agentId)?.push({
            name: r.release_name,
            version_number: r.release_version,
            is_default: r.release_is_default,
          });
        });
        setAgentReleasesMap(newMap);
      },
      (error: any) => {
        console.error("Error fetching agent releases:", error);
      }
    );
    return () => sub.unsubscribe();
  }, [apiKey]);

  // Helpers
  const isAgentSelected = (v?: string) => v?.startsWith("agent:");

  const parseAgentModelString = (v?: string) => {
    if (!isAgentSelected(v)) return { agentId: null, version: null, isRelease: false };
    const parts = v?.split(":") || [];
    if (parts.length < 2) return { agentId: null, version: null, isRelease: false };
    const agentId = parts[1];
    if (parts.length === 2) return { agentId, version: null, isRelease: false };
    const versionPart = parts.slice(2).join(":");
    if (versionPart.startsWith("release/")) {
      return { agentId, version: versionPart.replace("release/", ""), isRelease: true };
    }
    return { agentId, version: versionPart, isRelease: false };
  };

  const getSelectedAgent = (v?: string) => {
    const { agentId } = parseAgentModelString(v);
    if (!agentId) return null;
    return agents.find((a) => a.id?.toString() === agentId) || null;
  };

  const capitalizeFirstLetter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const groupModelsByProvider = (list: string[]) => {
    const grouped: Record<string, string[]> = {};
    list.forEach((m) => {
      const [provider] = m.split(":");
      if (!grouped[provider]) grouped[provider] = [];
      if (!grouped[provider].includes(m)) grouped[provider].push(m);
    });
    return grouped;
  };

  const getAgentDisplayOptions = (agent: Agent) => {
    const agentId = agent.id ? Number(agent.id) : null;
    if (!agentId) return [];
    const releases = agentReleasesMap.get(agentId) || [];
    const options: Array<{ value: string; label: string }> = [];
    options.push({
      value: `agent:${agentId}`,
      label: agent.agent_name || `Agent ${agentId}`,
    });
    releases.forEach((release) => {
      options.push({
        value: `agent:${agentId}:release/${release.name}`,
        label: `${release.name} (${agent.agent_name})`,
      });
    });
    return options;
  };

  // Filtered model lists
  const finetunedModels = useMemo(
    () => models.filter((m) => modelFinetuned[m]),
    [models, modelFinetuned]
  );

  const standardModels = useMemo(
    () => models.filter((m) => !modelFinetuned[m]),
    [models, modelFinetuned]
  );

  const { groupedModels, sortedProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(standardModels);
    const sorted = Object.keys(grouped).sort((a, b) => {
      if (a.toLowerCase() === "maitai") return -1;
      if (b.toLowerCase() === "maitai") return 1;
      return a.localeCompare(b);
    });
    return { groupedModels: grouped, sortedProviders: sorted };
  }, [standardModels]);

  const { groupedFinetunes, sortedFinetuneProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(finetunedModels);
    const sorted = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    return { groupedFinetunes: grouped, sortedFinetuneProviders: sorted };
  }, [finetunedModels]);

  // Category change handler
  const handleCategoryChange = (cat: Category) => {
    setSelectedCategory(cat);
    if (onChange) onChange("");
    if (onAgentParamsChange) onAgentParamsChange({ agent_id: null, settings: null });
  };

  // Item selection handler
  const handleValueChange = (newValue: string) => {
    if (isAgentSelected(newValue) && newValue.split(":").length === 2) {
      const agent = getSelectedAgent(newValue);
      if (agent?.id) {
        const agentId = Number(agent.id);
        if (onChange) onChange(`agent:${agentId}`);
        if (onAgentParamsChange) {
          onAgentParamsChange({
            agent_id: agentId,
            settings: {
              max_iterations: agent.max_iterations || null,
              execution_mode: "route",
              high_performance: true,
              disable_chaining: true,
              model: "openai:gpt-4o-mini",
              version: null,
            },
          });
        }
        return;
      }
    }

    if (onChange) onChange(newValue);
    if (onAgentParamsChange) {
      if (isAgentSelected(newValue)) {
        const agent = getSelectedAgent(newValue);
        const { version, isRelease } = parseAgentModelString(newValue);
        let versionInfo: { agent_version_number: string | null; agent_release_name: string | null } | null = null;
        if (version) {
          versionInfo = isRelease
            ? { agent_version_number: null, agent_release_name: version }
            : { agent_version_number: version, agent_release_name: null };
        }
        onAgentParamsChange({
          agent_id: agent?.id ? Number(agent.id) : null,
          settings: {
            max_iterations: agent?.max_iterations || null,
            execution_mode: "route",
            high_performance: true,
            disable_chaining: true,
            model: "openai:gpt-4o-mini",
            version: versionInfo,
          },
        });
      } else {
        onAgentParamsChange({ agent_id: null, settings: null });
      }
    }
  };

  const itemOptions = useMemo(() => {
    const options: Array<{ value: string; label: string; group: string }> = [];
    if (selectedCategory === "agents") {
      agents.forEach((agent) => {
        getAgentDisplayOptions(agent).forEach((opt) => {
          options.push({ ...opt, group: "Agents" });
        });
      });
    } else if (selectedCategory === "workflows") {
      workflows.forEach((wf) => {
        const ref = wf.workflow_ref_name;
        if (!ref) return;
        options.push({
          value: `workflow:${ref}`,
          label: wf.workflow_name || ref,
          group: "Workflows",
        });
      });
    } else if (selectedCategory === "finetunes") {
      const seen = new Set<string>();
      finetunedModels.forEach((model) => {
        const [, ...parts] = model.split(":");
        const name = parts.join(":");
        if (!seen.has(name)) {
          seen.add(name);
          options.push({ value: model, label: name, group: "Finetunes" });
        }
      });
    } else {
      sortedProviders.forEach((provider) => {
        groupedModels[provider].forEach((model) => {
          const [, ...parts] = model.split(":");
          options.push({ value: model, label: parts.join(":"), group: capitalizeFirstLetter(provider) });
        });
      });
    }
    return options;
  }, [
    selectedCategory,
    agents,
    agentReleasesMap,
    workflows,
    finetunedModels,
    sortedProviders,
    groupedModels,
  ]);

  const selectedDisplayLabel = useMemo(() => {
    if (!value) return null;
    const found = itemOptions.find((o) => o.value === value);
    return found?.label || value;
  }, [value, itemOptions]);

  const groupedItemOptions = useMemo(() => {
    const groups: Record<string, Array<{ value: string; label: string }>> = {};
    itemOptions.forEach((o) => {
      if (!groups[o.group]) groups[o.group] = [];
      groups[o.group].push({ value: o.value, label: o.label });
    });
    return groups;
  }, [itemOptions]);

  return (
    <div style={style}>
      <div className="flex items-center gap-2">
        <div className="w-[140px] flex-shrink-0">
          <Select
            value={selectedCategory}
            onValueChange={(v) => handleCategoryChange(v as Category)}
            disabled={disabled}>
            <SelectTrigger className="w-full focus:ring-maitai-green focus:border-maitai-green">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="agents">Agents</SelectItem>
              <SelectItem value="workflows">Workflows</SelectItem>
              <SelectItem value="finetunes">Finetunes</SelectItem>
              <SelectItem value="models">Models</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1">
          <Popover open={itemPickerOpen} onOpenChange={setItemPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={itemPickerOpen}
                disabled={disabled || isLoading}
                className="w-full justify-between font-normal">
                <span className="truncate">
                  {selectedDisplayLabel || placeholder}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search..." />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  {Object.entries(groupedItemOptions).map(([group, items]) => {
                    const groupCount = Object.keys(groupedItemOptions).length;
                    const showHeading = groupCount > 1;
                    return (
                    <CommandGroup
                      key={group}
                      heading={showHeading ? group : undefined}
                      className={
                        selectedCategory === "models"
                          ? ""
                          : "[&_[cmdk-group-heading]]:text-maitai-green"
                      }>
                      {items.map((item) => (
                        <CommandItem
                          key={item.value}
                          value={`${item.value}\u0001${item.label}`}
                          onSelect={() => {
                            handleValueChange(item.value === value ? "" : item.value);
                            setItemPickerOpen(false);
                          }}>
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === item.value ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="truncate">{item.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {selectedCategory === "agents" && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Checkbox
              id="show-subagents"
              checked={showSubagents}
              onCheckedChange={(checked) => setShowSubagents(checked === true)}
            />
            <Label
              htmlFor="show-subagents"
              className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap">
              Subagents
            </Label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelInput;
